import { useEffect, useState, useCallback } from "react";
import { MapPin, Plus, Pencil, Trash2, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { ConfirmDialog } from "@/components/ConfirmDialog";

const empty = { date: "", engineer_id: "", purpose: "", findings: "", status: "Scheduled" };

export function SiteVisitsPanel({ projectId }) {
  const { meta } = useAuth();
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/projects/${projectId}/site-visits`); setVisits(data); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty, date: new Date().toISOString().slice(0, 10) }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ date: (v.date || "").slice(0, 10), engineer_id: v.engineer_id || "", purpose: v.purpose || "", findings: v.findings || "", status: v.status }); setOpen(true); };
  const set = (k, val) => setForm((f) => ({ ...f, [k]: val }));

  const submit = async () => {
    if (!form.date) { toast.error("Visit date is required"); return; }
    setSaving(true);
    const payload = { ...form, engineer_id: form.engineer_id || null };
    try {
      if (editing) await api.put(`/site-visits/${editing.id}`, payload);
      else await api.post(`/projects/${projectId}/site-visits`, payload);
      toast.success(editing ? "Site visit updated" : "Site visit logged");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const doDelete = async () => { await api.delete(`/site-visits/${del.id}`); toast.success("Site visit deleted"); setDel(null); load(); };

  const engineers = meta?.users || [];

  return (
    <div className="space-y-4" data-testid="site-visits-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#0057A8]" /><h3 className="text-base font-semibold text-slate-800">Site Visits</h3></div>
        <Button data-testid="add-site-visit-btn" onClick={openNew} size="sm" className="gap-1.5 font-semibold"><Plus className="h-4 w-4" /> Log Visit</Button>
      </div>

      {loading ? (
        <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
      ) : visits.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white py-12 text-center">
          <p className="text-sm font-semibold text-slate-700">No site visits yet</p>
          <p className="mt-1 text-xs text-slate-400">Schedule or log a site visit to keep a field record for this project.</p>
          <Button onClick={openNew} className="mt-4 gap-1.5"><Plus className="h-4 w-4" /> Log First Visit</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {visits.map((v) => (
            <div key={v.id} data-testid={`site-visit-${v.id}`} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="flex items-center gap-1 text-sm font-semibold text-slate-800"><CalendarDays className="h-3.5 w-3.5 text-slate-400" />{formatDate(v.date)}</span>
                    <StatusBadge value={v.status} type="site_visit" dot />
                    {v.purpose && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600">{v.purpose}</span>}
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{v.engineer_name ? `Engineer: ${v.engineer_name}` : "No engineer assigned"}</p>
                  {v.findings && <p className="mt-1.5 text-sm text-slate-600">{v.findings}</p>}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button onClick={() => openEdit(v)} data-testid={`edit-site-visit-${v.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => setDel(v)} data-testid={`delete-site-visit-${v.id}`} className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit Site Visit" : "Log Site Visit"}</DialogTitle><DialogDescription>Record a scheduled or completed site visit for this project.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Date</Label><Input data-testid="sv-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="sv-status"><SelectValue /></SelectTrigger>
                <SelectContent>{(meta?.site_visit_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Site Engineer</Label>
              <Select value={form.engineer_id || ""} onValueChange={(v) => set("engineer_id", v)}><SelectTrigger data-testid="sv-engineer"><SelectValue placeholder="Select engineer" /></SelectTrigger>
                <SelectContent>{engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs font-semibold text-slate-600">Purpose</Label>
              <Select value={form.purpose || ""} onValueChange={(v) => set("purpose", v)}><SelectTrigger data-testid="sv-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>{(meta?.site_visit_purposes || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs font-semibold text-slate-600">Findings / Notes</Label><Textarea data-testid="sv-findings" rows={3} value={form.findings} onChange={(e) => set("findings", e.target.value)} placeholder="Observations, measurements, action items…" /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="sv-submit" onClick={submit} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save Visit" : "Log Visit"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!del} onOpenChange={() => setDel(null)} title="Delete site visit?" description="This removes the site visit record." confirmText="Delete" onConfirm={doDelete} />
    </div>
  );
}
